import { useEffect, useState } from "react";
import { MessageSquare, X } from "lucide-react";
import { FloatingActionButton } from "@lumanity/ui/primitives";
import { useDocumentViewer } from "@lumanity/ui/viewer";

import { cn } from "@/lib/utils";
import EvidenceChat from "./EvidenceChat";
import { useIsProjectStreaming } from "./chatStreamStore";

/**
 * Evidence Chat as a floating assistant: a button in the corner that opens the
 * chat in a window over the workspace. The chat stays mounted while closed, so
 * a draft or scroll position survives; replies keep streaming (chatStreamStore)
 * and the button pulses until they finish. While the source viewer is open
 * (a citation, opened on the right), the window moves to the left edge beside it.
 */
export default function ChatLauncher({ projectId }: Readonly<{ projectId: string }>) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const streaming = useIsProjectStreaming(projectId);
  const { doc: viewerDoc } = useDocumentViewer();
  const docked = !!viewerDoc;

  // Esc closes the window, unless something inside (the @-file picker) already handled it.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented && !viewerDoc) setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, viewerDoc]);

  return (
    <>
      <section
        aria-label="AI Assistant"
        aria-hidden={!open}
        data-testid="assistant-window"
        className={cn(
          "fixed inset-0 z-50 flex flex-col overflow-hidden bg-background shadow-2xl",
          "sm:inset-auto sm:bottom-24 sm:rounded-xl sm:border",
          docked ? "sm:left-6 md:max-w-[calc(40svw-3rem)]" : "sm:right-6",
          expanded ? "sm:h-[calc(100vh-8rem)] sm:w-[720px]" : "sm:h-[min(640px,calc(100vh-8rem))] sm:w-[420px]",
          "sm:max-w-[calc(100vw-3rem)]",
          open ? "animate-in fade-in-0 slide-in-from-bottom-4 duration-200" : "hidden",
        )}
      >
        <EvidenceChat
          projectId={projectId}
          onClose={() => setOpen(false)}
          expanded={expanded}
          onToggleExpand={() => setExpanded((e) => !e)}
        />
      </section>

      <FloatingActionButton
        position={docked ? "bottom-left" : "bottom-right"}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close AI Assistant" : "Open AI Assistant"}
        aria-expanded={open}
        title={streaming && !open ? "AI Assistant — a response is still generating" : "AI Assistant"}
        className={cn("bg-brand-teal text-white", open && "max-sm:hidden")}
      >
        {open ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
        {streaming && !open && (
          <span
            className="absolute right-1 top-1 h-3 w-3 animate-pulse rounded-full bg-brand-yellow ring-2 ring-background"
            data-testid="assistant-streaming"
          />
        )}
      </FloatingActionButton>
    </>
  );
}
